export default {
  title: 'Reports',
  description: 'View occupancy and revenue reports',
  select_property: 'Select Property',

  breadcrumbs: {
    details: 'Details',
  },

  occupancy: {
    title: 'Occupancy Report',
    total_units: 'Total Units',
    occupied_units: 'Occupied',
    vacant_units: 'Vacant',
    occupancy_rate: 'Occupancy Rate',
    trend: 'Occupancy Trend',
    breakdown: 'Property Breakdown',
  },

  revenue: {
    title: 'Revenue Report',
    total_invoiced: 'Total Invoiced',
    total_collected: 'Total Collected',
    outstanding: 'Outstanding',
    collection_rate: 'Collection Rate',
    chart_title: 'Revenue Over Time',
    breakdown: 'Property Breakdown',
  },

  filters: {
    property: 'Property',
    all_properties: 'All Properties',
    date_range: 'Date Range',
    from: 'From',
    to: 'To',
    period: 'Period',
    daily: 'Daily',
    monthly: 'Monthly',
    yearly: 'Yearly',
    quarterly: 'Quarterly',
    // RPT-3 additions
    toggle: 'Filters',
    all_periods: 'All Periods',
    date_preset: 'Date Range',
    preset_current_cycle: 'Current billing cycle',
    preset_full_year: 'Full year',
    preset_last3m: 'Last 3 Months',
    preset_last6m: 'Last 6 Months',
    preset_last12m: 'Last 12 Months',
    preset_ytd: 'Year to Date',
    preset_custom: 'Custom',
  },

  labels: {
    invoiced: 'Invoiced',
    collected: 'Collected',
    property_name: 'Property',
    total: 'Total',
    occupied: 'Occupied',
    rate: 'Rate',
    refreshing: 'Refreshing…',
  },

  empty: {
    title: 'No data available',
    description: 'Report data will appear once you have properties and invoices.',
  },

  setup_guide: {
    occupancy: {
      title: 'Occupancy Report Setup',
      description: 'You need properties with units and leases to view occupancy data.',
    },
    revenue: {
      title: 'Revenue Report Setup',
      description: 'You need generated invoices to view revenue data.',
    },
  },

  actions: {
    print: 'Print',
    export_csv: 'Export CSV',
  },

  // RPT-9 5a — Parking Occupancy (locked design 2026-05-28)
  parking_occupancy: {
    title: 'Parking Occupancy',
    subtitle: 'Zone utilisation snapshot',
    export_csv: 'Export CSV',
    filters_label: 'Filters',
    filter_property: 'Property',
    filter_zone: 'Zone',
    all_zones: 'All zones',
    kpi_spaces: 'Total Spaces',
    kpi_occupied: 'Occupied',
    kpi_vacant: 'Vacant',
    kpi_rate: 'Occupancy',
    chart_section_title: 'Occupancy % by zone',
    table_section_title: 'Zone detail',
    col_zone: 'Zone',
    col_spaces: 'Spaces',
    col_occupied: 'Occupied',
    col_vacant: 'Vacant',
    col_rate: 'Rate',
    covered_badge: 'covered',
    no_zones: 'No parking zones',
  },

  // RPT-9 5b — Parking Revenue (locked design 2026-05-28)
  parking_revenue: {
    title: 'Parking Revenue',
    subtitle: 'Income by stream over time',
    export_csv: 'Export CSV',
    filters_label: 'Filters',
    filter_property: 'Property',
    filter_stream: 'Stream',
    kpi_total: 'Total Parking Revenue',
    kpi_tenant: 'Tenant Add-on',
    kpi_external: 'External',
    kpi_outstanding: 'Outstanding',
    chart_section_title: 'Parking revenue / month',
    legend_tenant: 'Tenant add-on',
    legend_external: 'External',
    table_section_title: 'By income stream',
    col_stream: 'Stream',
    col_vehicles: 'Vehicles',
    col_revenue: 'Revenue',
    col_outstanding: 'Outstanding',
    stream_tenant: 'Tenant add-on',
    stream_external: 'External',
    no_revenue: 'No parking revenue in this period',
  },

  // RPT-9 5c — Parking Registry (locked design 2026-05-28)
  parking_registry: {
    title: 'Parking Registry',
    subtitle: 'Renew permits, chase registrations',
    export_csv: 'Export CSV',
    filters_label: 'Filters',
    filter_property: 'Property',
    filter_expiring_within: 'Expiring within',
    window_30: '30 days',
    window_60: '60 days',
    window_90: '90 days',
    kpi_registered: 'Registered Vehicles',
    kpi_unregistered: 'Unregistered Units',
    kpi_expiring: 'Expiring ≤30d',
    unregistered_section_title: 'Unregistered units',
    unregistered_section_subtitle: 'Tenants with active leases and no registered vehicle',
    expiring_section_title: 'Expiring / expired permits',
    expiring_section_subtitle: 'Permits expiring within the selected window',
    col_unit: 'Unit',
    col_property: 'Property',
    col_tenant: 'Tenant',
    col_lease_since: 'Lease Since',
    col_plate: 'Plate',
    col_unit_property: 'Unit / Property',
    col_expires: 'Expires',
    col_days_left: 'Days Left',
    days_left_one: '{{count}}d left',
    days_left_other: '{{count}}d left',
    expired_badge: 'Expired',
    no_unregistered_units: 'No unregistered units',
    no_expiring_permits: 'No expiring permits',
  },

  expense_section_title: 'Expense & Financial Reports',

  expense_summary: {
    title: 'Expense Summary',
    description: 'Overview of expenses by category and property',
    total_expenses: 'Total Expenses',
    by_category: 'By Category',
    by_property: 'By Property',
    percentage: '% of Total',
  },

  expense_trend: {
    title: 'Expense Trend',
    description: 'Monthly expense trends by category',
  },

  profit_loss: {
    basis_label: 'Income basis',
    basis_billed: 'Based on invoices',
    basis_received: 'Based on payments received',
    expense_including_vat: 'Expenses including VAT',
    expense_excluding_vat: 'Expenses excluding VAT',
    net_basis: '{{income}} − {{expense}}',
    grouping_hint: 'Select trend lines. Totals keep the report property scope.',
    expense_categories_scope: 'Expense categories for this report scope',
    title: 'Profit & Loss',
    description: 'Revenue vs expenses analysis',
    // Legacy keys (backward compat)
    revenue: 'Revenue',
    revenue_collected: 'Revenue (Cash Collected)',
    expenses: 'Expenses',
    net_profit: 'Net Profit',
    margin: 'Margin %',
    // RPT-7 Glance + Details keys (from design doc locked list)
    subtitle: 'Income minus costs, the bottom line',
    default_scope_hint: 'This period · All properties',
    scope_hint_property: 'This period · {{property}}',
    details_cta: 'By property & filters',
    kpi_income: 'Income',
    kpi_expense: 'Expense',
    kpi_net: 'Net Profit',
    kpi_margin: 'Margin',
    trend_section_title: 'Net Profit Trend (6 months)',
    trend_title: 'Net Profit Trend',
    target_line_label: 'Target',
    grouping_label: 'Grouping',
    grouping_all: 'All properties',
    granularity_label: 'Granularity',
    daily: 'Daily',
    monthly: 'Monthly',
    yearly: 'Yearly',
    filter_unit: 'Unit',
    hero_swap_label: 'Chart type',
    swap_line: 'Line',
    swap_waterfall: 'Waterfall',
    swap_treemap: 'Treemap',
    breakdown_section_title: 'Breakdown by property',
    col_income: 'Income',
    col_expense: 'Expense',
    col_net: 'Net',
    col_margin: 'Margin',
    income_categories: 'Income categories',
    expense_categories: 'Expense categories',
    expense_categories_portfolio: 'Expense categories (all properties combined)',
    treemap_leaf_label: 'Expense category',
  },

  collection_rate: {
    title: 'Collection Rate',
    description: 'Monthly invoice collection rate trend',
    invoiced: 'Invoiced',
    collected: 'Collected',
    rate: 'Rate',
  },

  empty_state: {
    no_data: {
      title: 'No data available',
      description: 'Adjust the filters to see results.',
      action: 'Adjust Filters',
    },
    no_properties: {
      title: 'No properties yet',
      description: 'Add a property to start seeing report data.',
      action: 'Go to Properties',
    },
    error: {
      title: 'Failed to load data',
      description: 'Something went wrong loading this report.',
      action: 'Retry',
    },
  },

  week_lock: {
    header: 'Weeks Locked {{locked}}/{{total}}',
  },

  // Reports index hub (RPT-4)
  index: {
    rent_roll: 'Monthly rent roll',
    // 2026-09-29: the tile carries no description line any more, so the label
    // says what the figure is (rent the vacant units would bring each month).
    vacancy_gap: 'Rent lost to vacancies',
    // Quick-summary strip tile labels (reused by BusinessSnapshot on dashboard)
    qs_billed: 'Billed',
    qs_unpaid: 'Unpaid',
    qs_overdue: 'Overdue',
    nothing_billed: 'No invoices issued for this period yet',
    generate_invoices: 'Generate invoices',
    qs_revenue: 'Received',
    qs_collection_rate: 'Collection Rate',
    qs_occupancy: 'Occupancy Rate',
    qs_net_profit: 'Net Profit',
    // EP-149 T149-081 (MON-7 D): account-wide balance, not period-scoped like
    // the tiles above.
    qs_deposits_held: 'Deposits held',

    // Group card titles
    group_collections: 'Collections',
    group_occupancy: 'Occupancy',
    group_profitability: 'Profitability',
    group_records: 'Management Records',
    group_parking: 'Parking',

    // Group card report-preview labels (joined with ·)
    preview_collections: 'Collection Stats · Tenant Scorecard · Overdue Invoices',
    preview_occupancy: 'Occupancy Stats · Vacant Units',
    preview_profitability: 'Profit & Loss',
    preview_records: 'Timesheets · Maintenance · CAF · Bank Recon · Fin. Statements',
    preview_parking: 'Parking Occupancy · Revenue · Registry',

    // Group landing page descriptions
    desc_collections: 'Invoiced vs collected, tenant quality, and overdue tracking',
    desc_occupancy: 'Occupancy rates, trends, and vacant unit details',
    desc_profitability: 'Income minus costs, the bottom line',
    desc_records: 'Staff hours, work orders, fees, and bank reconciliation',
    desc_parking: 'Zone utilisation, income, and permit registry',
  },

  // RPT-5: Collection Stats (1a)
  collection_stats: {
    title: 'Collection Stats',
    subtitle: 'Invoiced vs collected, collection health over time',
    kpi_invoiced: 'Total Invoiced',
    kpi_collected: 'Total Collected',
    kpi_rate: 'Collection Rate',
    kpi_avg_days: 'Avg Days to Collect',
    rate_healthy_hint: '✓ ≥ 90%',
    trend_section_title: 'Collection Rate Trend (6 months)',
    trend_title: 'Collection Rate Trend',
    breakdown_section_title: 'Per-property breakdown',
    details_cta: 'Per-property & filters',
    default_scope_hint: 'Current period · All properties',
    grouping_label: 'Grouping',
    grouping_all: 'All properties',
    col_avg_days: 'Avg Days',
  },

  // RPT-5: Tenant Scorecard (1b)
  tenant_scorecard: {
    title: 'Tenant Scorecard',
    subtitle: 'Renewal quality signal per tenant',
    kpi_total_tenants: 'Total Tenants',
    kpi_avg_score: 'Avg Score',
    kpi_up_renewal: 'Up for Renewal',
    kpi_watch: 'Watch (<50)',
    quality_by_property_title: 'Tenant quality by property',
    band_strong: 'Strong',
    band_ok: 'OK',
    band_watch: 'Watch',
    tenant_count_one: '{{count}} tenant',
    tenant_count_other: '{{count}} tenants',
    axis_payment: 'Payment',
    axis_maintenance: 'Maintenance',
    axis_parcel: 'Parcel',
    overall_label: 'Overall',
    rail_search_placeholder: 'Search tenants…',
    detail_payment_facts: 'Payment history',
    detail_maint_facts: 'Maintenance',
    detail_parcel_facts: 'Parcels',
    lease_term_label: 'Lease term',
    view_lease_cta: 'View lease',
    default_scope_hint: 'Last 12 months · All properties',
    lookback_label: 'Lookback window',
    near_expiry_badge: 'Renewing soon',
    fact_total_invoices: 'Total Invoices',
    fact_on_time: 'On Time',
    fact_overdue: 'Overdue',
    fact_avg_days_late: 'Avg Days Late',
    fact_total_requests: 'Total Requests',
    fact_resolved: 'Resolved',
    fact_total_parcels: 'Total Parcels',
    fact_avg_pickup_days: 'Avg Pickup Days',
    lease_start_label: 'Start',
    lease_end_label: 'End',
  },

  // RPT-5: Overdue Invoices (1c)
  overdue: {
    title: 'Overdue Invoices',
    subtitle: 'Debt-collection quality and chase list',
    export_csv: 'Export CSV',
    filters_label: 'Filters',
    kpi_total_unpaid: 'Unpaid',
    kpi_total_overdue: 'Overdue',
    kpi_count: 'Overdue Count',
    kpi_tenant_on_time: 'Tenant On-Time %',
    kpi_admin_recovery: 'Admin Recovery %',
    portfolio_hint: 'Portfolio',
    quality_chart_title: 'Debt-collection quality by property',
    series_tenant_on_time: 'Tenant on-time',
    series_admin_recovery: 'Admin recovery',
    chase_priority_hint: 'Weak on both axes → chase first',
    col_invoice: 'Invoice',
    col_tenant: 'Tenant',
    col_unit: 'Unit',
    col_property: 'Property',
    col_amount: 'Amount',
    col_paid: 'Paid',
    col_balance: 'Balance',
    col_due: 'Due',
    col_days_late: 'Late',
    days_late: '{{count}}d late',
    sort_worst_first: 'Worst first',
    no_overdue: 'No overdue invoices',
    row_aria_label: 'Invoice {{number}}',
    rate_unavailable: 'Rate unavailable: no qualifying invoices',
    aging_title: 'Invoice aging by property',
    no_aging: 'No invoices in this period',
    aging_count_one: '{{count}} invoice',
    aging_count_other: '{{count}} invoices',
    aging_buckets: {
      not_yet_due: 'Not yet due',
      '1_30': '1–30 days',
      '31_60': '31–60 days',
      '61_90': '61–90 days',
      over_90: 'Over 90 days',
    },
  },

  // RPT-6: Occupancy Stats (2a)
  occupancy_stats: {
    title: 'Occupancy Stats',
    subtitle: 'Occupancy rate and trend over time',
    kpi_rate: 'Occupancy Rate',
    kpi_occupied: 'Occupied Units',
    kpi_vacant: 'Vacant Units',
    kpi_total: 'Total Units',
    healthy_threshold_hint: '✓ ≥ 85%',
    trend_section_title: 'Occupancy Rate Trend (6 months)',
    trend_title: 'Occupancy Rate Trend',
    breakdown_section_title: 'Per-property breakdown',
    details_cta: 'Per-property & filters',
    default_scope_hint: 'Last 6 months · All properties',
    grouping_label: 'Grouping',
    grouping_all: 'All properties',
    col_occupied: 'Occupied',
    col_vacant: 'Vacant',
    col_rate: 'Rate',
    col_delta: 'Δ vs Prior',
    granularity_label: 'Granularity',
    daily: 'Daily',
    monthly: 'Monthly',
    yearly: 'Yearly',
    // TODO RPT-10 heatmap swap
  },

  // RPT-6: Vacant Units (2b)
  vacant: {
    title: 'Vacant Units',
    subtitle: 'Empty units and estimated lost revenue',
    kpi_vacant_count: 'Vacant Units',
    kpi_vacancy_rate: 'Vacancy Rate',
    kpi_avg_days: 'Avg Days Vacant',
    kpi_lost_rent: 'Est. Lost Rent / mo',
    chart_title: 'Est. Lost Rent by Property',
    list_section_title: 'Vacant units (longest vacant first)',
    col_unit: 'Unit',
    col_property: 'Property',
    col_room_type: 'Room Type',
    col_rent: 'Rent',
    col_vacant_since: 'Vacant Since',
    col_days_vacant: 'Days Vacant',
    days_badge_error: '>60d',
    days_badge_warning: '≤60d',
    no_vacant_units: 'All units are currently occupied.',
    export_csv: 'Export CSV',
    filters_label: 'Filters',
    vacant_since_cutoff_label: 'Vacant since (cutoff)',
    unit_type_label: 'Unit Type',
    all_unit_types: 'All types',
    default_scope_hint: 'All properties · All time',
  },

  // Level-2 group landing pages (RPT-4)
  report_cards: {
    collection_stats: {
      title: 'Collection Stats',
      description: 'Invoiced vs collected amounts over time',
    },
    tenant_scorecard: {
      title: 'Tenant Scorecard',
      description: 'Renewal quality score per tenant',
    },
    overdue_invoices: {
      title: 'Overdue Invoices',
      description: 'Who is past due and by how much',
    },
    occupancy_stats: {
      title: 'Occupancy Stats',
      description: 'Occupancy rate and trend over time',
    },
    vacant_units: {
      title: 'Vacant Units',
      description: 'Empty units and days vacant',
    },
    profit_loss: {
      title: 'Profit & Loss',
      description: 'Income minus costs, the bottom line',
    },
    timesheets: {
      title: 'Timesheets',
      description: 'Staff hours logged per period',
    },
    maintenance: {
      title: 'Maintenance',
      description: 'Work-order activity and completion rate',
    },
    caf_collection: {
      title: 'CAF Collection',
      description: 'Common-area fee billed vs collected',
    },
    bank_reconciliation: {
      title: 'Bank Reconciliation',
      description: 'Matched vs unmatched transactions',
    },
    financial_statements: {
      title: 'Financial Statements',
      description: 'GL statements for accounting export',
    },
    parking_occupancy: {
      title: 'Parking Occupancy',
      description: 'Zone fill rate and slot utilisation',
    },
    parking_revenue: {
      title: 'Parking Revenue',
      description: 'Parking income by category',
    },
    parking_registry: {
      title: 'Parking Registry',
      description: 'Permits and vehicle registrations',
    },
  },

  // ===== RPT-8 — Juristic / Management Records reports (4a-4e) =====
  records: {
    timesheets: {
      title: 'Timesheets',
      subtitle: 'Staff hours and weekly lock status',
      export_csv: 'Export CSV',
      filters_label: 'Filters',
      filter_property: 'Property',
      filter_staff: 'Staff',
      kpi_total_hours: 'Total Hours',
      kpi_staff_count: 'Staff Count',
      kpi_avg_hours: 'Avg Hours/Staff',
      kpi_weeks_locked: 'Weeks Locked',
      week_lock_strip_title: 'Week lock status',
      week_locked: 'Locked',
      week_open: 'Open',
      chart_section_title: 'Hours by Staff',
      table_section_title: 'Staff hours',
      col_staff: 'Staff',
      col_role: 'Role',
      col_hours: 'Hours',
      col_days_worked: 'Days Worked',
      no_entries: 'No timesheet entries in this period',
    },
    maintenance: {
      title: 'Maintenance',
      subtitle: 'Maintenance request volume by status',
      export_csv: 'Export CSV',
      filters_label: 'Filters',
      filter_property: 'Property',
      filter_status: 'Status',
      kpi_total: 'Total Requests',
      kpi_fixed: 'Fixed',
      kpi_open: 'Open',
      kpi_closed: 'Closed',
      volume_section_title: 'Requests per month',
      legend_open: 'Open',
      legend_fixed: 'Fixed',
      legend_closed: 'Closed',
      table_section_title: 'Maintenance requests',
      col_req: 'Req#',
      col_property_unit: 'Property / Unit',
      col_category: 'Category',
      col_status: 'Status',
      col_dates: 'Opened / Closed',
      status_open: 'Open',
      status_fixed: 'Fixed',
      status_closed: 'Closed',
      filter_status_all: 'All statuses',
      filter_status_open: 'Open',
      filter_status_in_progress: 'In Progress',
      filter_status_completed: 'Completed',
      no_requests: 'No maintenance requests in this period',
    },
    caf: {
      title: 'CAF Collection',
      subtitle: 'Common-area fee billed vs collected',
      export_csv: 'Export CSV',
      filters_label: 'Filters',
      filter_property: 'Property',
      filter_unit: 'Unit',
      view_label: 'View',
      view_money: 'Money',
      view_records: 'Records',
      kpi_billed: 'CAF Billed',
      kpi_collected: 'Collected',
      kpi_rate: 'Collection Rate',
      kpi_outstanding: 'Outstanding',
      chart_money_title: 'CAF Billed / month',
      chart_records_title: 'CAF Invoices / month',
      legend_collected: 'Collected',
      legend_outstanding: 'Outstanding',
      legend_paid: 'Paid',
      legend_partial: 'Partial',
      legend_overdue: 'Overdue',
      detail_money_title: 'By property / unit',
      detail_records_title: 'CAF invoices',
      col_property: 'Property / Unit',
      col_billed: 'Billed',
      col_collected: 'Collected',
      col_outstanding: 'Outstanding',
      col_rate: 'Rate',
      rec_col_invoice: 'CAF Inv#',
      rec_col_owner: 'Owner',
      rec_col_paid: 'Paid',
      rec_col_paid_date: 'Paid Date',
      rec_col_status: 'Status',
      status_paid: 'Paid',
      status_partial: 'Partial',
      status_overdue: 'Overdue',
      no_rows: 'No CAF billing in this period',
      // T-A1C-3 — property costs block (Expense rows on the condo property, not CAF
      // revenue). No allowlist: cost_category_label wraps whatever category the account
      // has configured, never a bare category slug (naming-overlap with
      // CommonAreaFee#fee_type — see docs/ep-111-condominium-property-type.md §0 C1).
      costs_section_title: 'CAF-related property costs',
      costs_section_caption: 'Property-level operating costs, not the same as Common Area Fee revenue billed to co-owners',
      costs_total_label: 'Total costs this period',
      costs_col_category: 'Category',
      costs_col_property: 'Property',
      costs_col_total: 'Total',
      cost_category_label: '{{category}} (cost)',
      costs_empty: 'No property costs in this period',
    },
    bank_recon: {
      title: 'Bank Reconciliation',
      subtitle: 'Matched vs unmatched transactions',
      filters_label: 'Filters',
      filter_bank: 'Bank',
      kpi_matched: 'Matched',
      kpi_unmatched: 'Unmatched',
      kpi_match_rate: 'Match Rate',
      kpi_unmatched_amount: 'Unmatched ฿',
      chart_section_title: 'Transactions / month',
      legend_matched: 'Matched',
      legend_unmatched: 'Unmatched',
      legend_needs_review: 'Needs review',
      detail_section_title: 'Transaction detail',
      detail_link_hint: 'Line items, matching & export are on the reconciliation page',
      open_reconciliation: 'Open Bank Reconciliation',
      unmatched_need_attention: '{{count}} unmatched need attention',
      bank_all: 'All banks',
      bank_scb: 'SCB',
      bank_kbank: 'KBank',
      bank_bbl: 'BBL',
    },
    financial_statements: {
      title: 'Financial Statements',
      subtitle: 'Income statement and balance sheet',
      print: 'Print',
      export_csv: 'Export CSV',
      filters_label: 'Filters (income statement)',
      filters_scope_note: "The date range applies to the income statement only. The balance sheet shows today's position.",
      filter_property: 'Property',
      period_label: 'Period',
      pl_period_range: 'Period: {{from}} – {{to}}',
      bs_as_of_date: 'As of {{date}}',
      pl_section_title: 'Income Statement',
      bs_section_title: 'Balance Sheet',
      revenue: 'Revenue',
      expenses: 'Expenses',
      net_income: 'Net Income',
      assets: 'Assets',
      liabilities: 'Liabilities',
      equity: 'Equity',
      total_revenue: 'Total Revenue',
      total_expenses: 'Total Expenses',
      total_assets: 'Total Assets',
      total_liab_equity: 'Liab. + Equity',
      derived_note: 'Derived from invoices and expenses (no posted ledger entries this period)',
      no_data: 'No posted journal entries in this period',
      lines: {
        invoiced_revenue: 'Invoiced revenue',
        recorded_expenses: 'Recorded expenses',
        retained_earnings: 'Retained earnings',
        current_year_earnings: 'Current year earnings',
      },
    },
  },
}
